/**
 * Server functions for CIH playbooks.
 *
 * Playbooks are thought-leader-attributed business/marketing frameworks managed
 * by admins, rendered with a Person byline from the attributed thought leader.
 * Lifecycle transitions delegate to content-lifecycle helpers.
 *
 * Aliases: playbooks, cih playbooks, playbook crud, playbook lifecycle
 */

import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { permissionMiddleware } from "~/lib/permission-middleware";
import {
  createPlaybookInputSchema,
  updatePlaybookInputSchema,
  type PlaybookListRow,
  type PlaybookRow,
  type PlaybookWithRelations,
} from "~/lib/server/cih/playbooks";

// ============================================================================
// Types (re-exported from shared server module)
// ============================================================================

export type {
  PlaybookListRow,
  PlaybookRow,
  PlaybookWithRelations,
} from "~/lib/server/cih/playbooks";

// ============================================================================
// Query Keys
// ============================================================================

export const playbookQueryKeys = {
  all: ["playbooks"] as const,
  list: (status?: string) => ["playbooks", "list", status ?? "all"] as const,
  detail: (id: string) => ["playbooks", id] as const,
  published: (slug: string) => ["playbooks", "published", slug] as const,
  publishedAll: ["playbooks", "published", "all"] as const,
};

// ============================================================================
// Server Functions — Reads (delegate to shared server layer)
// ============================================================================

/**
 * List all playbooks (admin). Excludes draft-of rows by default.
 * Optionally filter by status.
 */
export const getPlaybooksFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:read")])
  .validator(
    z
      .object({
        status: z.string().optional(),
      })
      .optional(),
  )
  .handler(async ({ data }): Promise<PlaybookListRow[]> => {
    const { listPlaybooks } = await import("~/lib/server/cih/playbooks");
    return listPlaybooks(data);
  });

/**
 * Get a single playbook by ID (admin).
 */
export const getPlaybookFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:read")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data }): Promise<PlaybookRow | null> => {
    const { getPlaybookById } = await import("~/lib/server/cih/playbooks");
    return getPlaybookById(data);
  });

/**
 * Get a published playbook by slug (public — no auth required).
 */
export const getPublishedPlaybookFn = createServerFn({ method: "POST" })
  .validator(z.object({ slug: z.string() }))
  .handler(async ({ data }): Promise<PlaybookRow | null> => {
    const { getPublishedPlaybookBySlug } =
      await import("~/lib/server/cih/playbooks");
    return getPublishedPlaybookBySlug(data);
  });

/**
 * Get a published playbook by slug with the joined thought leader for the
 * Person byline. Public — no auth required.
 */
export const getPublishedPlaybookWithRelationsFn = createServerFn({
  method: "POST",
})
  .validator(z.object({ slug: z.string() }))
  .handler(async ({ data }): Promise<PlaybookWithRelations | null> => {
    const { getPublishedPlaybookWithRelations } =
      await import("~/lib/server/cih/playbooks");
    return getPublishedPlaybookWithRelations(data);
  });

/**
 * List all published playbooks (public — no auth required).
 */
export const getPublishedPlaybooksFn = createServerFn({
  method: "POST",
}).handler(async () => {
  const { getPublishedPlaybooks } = await import("~/lib/server/cih/playbooks");
  return getPublishedPlaybooks();
});

// ============================================================================
// Server Functions — Writes (delegate to shared server layer)
// ============================================================================

/**
 * Create a new playbook (admin). authorId is set from the session.
 */
export const createPlaybookFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(createPlaybookInputSchema)
  .handler(async ({ data, context }): Promise<PlaybookRow> => {
    const { createPlaybook } = await import("~/lib/server/cih/playbooks");
    return createPlaybook(data, { userId: context.user.id });
  });

/**
 * Update playbook fields (admin, partial update).
 */
export const updatePlaybookFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(updatePlaybookInputSchema)
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const { updatePlaybook } = await import("~/lib/server/cih/playbooks");
    return updatePlaybook(data);
  });

// ============================================================================
// Server Functions — Lifecycle (delegate to shared server layer)
// ============================================================================

/**
 * Admin self-publish a draft playbook (skips review).
 */
export const publishPlaybookFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { publishPlaybook } = await import("~/lib/server/cih/playbooks");
    return publishPlaybook(data, { userId: context.user.id });
  });

/**
 * Archive a published playbook.
 */
export const archivePlaybookFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { archivePlaybook } = await import("~/lib/server/cih/playbooks");
    return archivePlaybook(data, { userId: context.user.id });
  });

/**
 * Create an editable draft of a published playbook.
 */
export const createPlaybookDraftFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ publishedId: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<PlaybookRow> => {
    const { createDraftOfPublishedPlaybook } =
      await import("~/lib/server/cih/playbooks");
    return createDraftOfPublishedPlaybook(data, { userId: context.user.id });
  });

/**
 * Promote a draft back onto its published parent.
 */
export const promotePlaybookDraftFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ draftId: z.string().uuid() }))
  .handler(async ({ data }): Promise<PlaybookRow> => {
    const { promotePlaybookDraft } = await import("~/lib/server/cih/playbooks");
    return promotePlaybookDraft(data);
  });

/**
 * Restore an archived playbook to draft.
 */
export const restorePlaybookFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { restorePlaybook } = await import("~/lib/server/cih/playbooks");
    return restorePlaybook(data, { userId: context.user.id });
  });

/**
 * Approve an in_review playbook (publish it).
 */
export const approvePlaybookFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { approvePlaybook } = await import("~/lib/server/cih/playbooks");
    return approvePlaybook(data, { userId: context.user.id });
  });

/**
 * Return an in_review playbook to draft with feedback.
 */
export const returnPlaybookToDraftFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid(), note: z.string().min(1) }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { returnPlaybookToDraft } =
      await import("~/lib/server/cih/playbooks");
    return returnPlaybookToDraft(data, { userId: context.user.id });
  });

/**
 * Submit a playbook draft for review.
 */
export const submitPlaybookForReviewFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { submitPlaybookForReview } =
      await import("~/lib/server/cih/playbooks");
    return submitPlaybookForReview(data, { userId: context.user.id });
  });

// ============================================================================
// Query Options
// ============================================================================

export const playbooksQueryOptions = (status?: string) =>
  queryOptions({
    queryKey: playbookQueryKeys.list(status),
    queryFn: () => getPlaybooksFn({ data: { status } }),
  });

export const playbookQueryOptions = (id: string) =>
  queryOptions({
    queryKey: playbookQueryKeys.detail(id),
    queryFn: () => getPlaybookFn({ data: { id } }),
  });

export const publishedPlaybookQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: playbookQueryKeys.published(slug),
    queryFn: () => getPublishedPlaybookFn({ data: { slug } }),
  });

export const publishedPlaybookWithRelationsQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: [...playbookQueryKeys.published(slug), "relations"] as const,
    queryFn: () => getPublishedPlaybookWithRelationsFn({ data: { slug } }),
  });

export const publishedPlaybooksQueryOptions = () =>
  queryOptions({
    queryKey: playbookQueryKeys.publishedAll,
    queryFn: () => getPublishedPlaybooksFn(),
  });
